import React, { useCallback, useMemo } from 'react';
import { Calendar, EventProps, ToolbarProps, View } from 'react-big-calendar';
import { differenceInMinutes, parseISO } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { Reservation } from '../../types/reservation';
import { ReservationType } from '../../types/reservationType';
import { ReservationEvent } from '../../components/ReservationPanel/ReservationPanel';
import { ReservationCalendarEvent } from '../../components/ReservationCalendarEvent/ReservationCalendarEvent';
import { useReservationTypes } from '../../hooks/AppData/useReservationTypes';
import { usePullToRefreshContext } from '../../context/PullToRefreshContext';
import { calendarLocalizer } from '../../utils/calendarUtils';
import { getOrderedNoOverlapLayout } from '../../utils/orderedNoOverlapLayout';
import {
  adjustEndTimeToMaxLimit,
  CalendarRestrictions,
  FALLBACK_DEFAULT_DURATION,
  InitialHours,
  isClosedDay,
  isInRestHour,
} from './calendarRestrictions';
import { CALENDAR_VIEWS, CalendarEvent, HIDDEN_WEEKDAYS_CALENDAR_VIEWS, reservationsCalendarMessages } from './calendarViews';
import { ReservationsCalendarToolbar } from './ReservationsCalendarToolbar';
import { dataFillProps } from '../../utils/dataFill';

const isNativePlatform = Capacitor.isNativePlatform();

export const ReservationsCalendar = ({
  events,
  currentDate,
  currentView,
  onNavigate: handleNavigate,
  onView: handleViewChange,
  onRangeChange: handleRangeChange,
  hiddenWeekdays,
  calendarRestrictions,
  reservationTypeFromId,
  hasMultipleBranches,
  createButtonReservationType,
  setCreateButtonReservationType,
  uniqueReservationTypes,
  isFullscreen,
  onToggleFullscreen: toggleFullscreen,
  onOpenReservation: onView,
  onSelectNewSlot,
}: {
  events: CalendarEvent[];
  currentDate: Date;
  currentView: View;
  onNavigate: (newDate: Date) => void;
  onView: (view: View) => void;
  onRangeChange: (range: Date[] | { start: Date; end: Date }, view?: View) => void;
  hiddenWeekdays: readonly number[];
  calendarRestrictions: CalendarRestrictions;
  reservationTypeFromId: ReturnType<typeof useReservationTypes>['reservationTypeFromId'];
  hasMultipleBranches: boolean;
  createButtonReservationType: ReservationType | null;
  setCreateButtonReservationType: (type: ReservationType) => void;
  uniqueReservationTypes: ReservationType[];
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onOpenReservation: (reservation: Reservation | undefined) => void;
  onSelectNewSlot: (slot: { initialHours: InitialHours; temporaryEvent: ReservationEvent }) => void;
}) => {
  const calendarViews = hiddenWeekdays.length ? HIDDEN_WEEKDAYS_CALENDAR_VIEWS : CALENDAR_VIEWS;
  const calendarViewProps = { hiddenWeekdays };
  const { disabledPullToRefresh, setDisabledPullToRefresh } = usePullToRefreshContext();

  const eventPropGetter = useCallback(
    (event: CalendarEvent) => {
      const reservationType = reservationTypeFromId(event.reservationTypeId);
      const typeColor = event.blocked ? 'bluegray' : (reservationType?.color ?? 'bluegray');
      return dataFillProps(typeColor);
    },
    [reservationTypeFromId],
  );

  const slotPropGetter = useCallback(
    (date: Date) => {
      // Comprobar si la hora está en el periodo de descanso
      if (isInRestHour(date, calendarRestrictions.restHour)) {
        return {
          className: 'disabled-day',
          style: { backgroundColor: 'var(--surface-sunken)', color: 'var(--text-subtle)' },
        };
      }
      return {};
    },
    [calendarRestrictions.restHour],
  );

  const dayPropGetter = useCallback(
    (date: Date) => {
      if (isClosedDay(date, calendarRestrictions)) {
        return {
          className: 'disabled-day',
          style: { backgroundColor: 'var(--surface-sunken)', color: 'var(--text-subtle)' },
        };
      }
      return {};
    },
    [calendarRestrictions],
  );

  const CalendarToolbarComponent = useCallback(
    (props: ToolbarProps<CalendarEvent, object>) => (
      <ReservationsCalendarToolbar
        {...props}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        hiddenWeekdays={hiddenWeekdays}
      />
    ),
    [isFullscreen, toggleFullscreen, hiddenWeekdays],
  );

  const CalendarEventComponent = useCallback(
    (props: EventProps<CalendarEvent>) => {
      const reservationType = reservationTypeFromId(props.event.reservationTypeId);
      if (!reservationType) return null;
      return <ReservationCalendarEvent {...props} reservationType={reservationType} showBranch={hasMultipleBranches} />;
    },
    [reservationTypeFromId, hasMultipleBranches],
  );

  // Places simultaneous reservations left to right following the reservation
  // type order (lowest order leftmost).
  const dayLayoutAlgorithm = useMemo(
    () =>
      getOrderedNoOverlapLayout<CalendarEvent>(
        (event) => reservationTypeFromId(event.reservationTypeId)?.order ?? Number.MAX_SAFE_INTEGER,
      ),
    [reservationTypeFromId],
  );

  const hasBigSlots =
    createButtonReservationType?.fixedDuration || (!createButtonReservationType && uniqueReservationTypes[0]?.fixedDuration);
  return (
    <Calendar<CalendarEvent>
      className={classNames('reservation-calendar no-event-label', {
        'big-slots': hasBigSlots,
      })}
      dayLayoutAlgorithm={dayLayoutAlgorithm}
      localizer={calendarLocalizer}
      events={events}
      formats={{
        dayRangeHeaderFormat: ({ start, end }, culture, localizer) => {
          const startMonth = localizer?.format(start, 'MM', culture);
          const endMonth = localizer?.format(end, 'MM', culture);
          if (startMonth !== endMonth) {
            return `${localizer?.format(start, "dd 'de' MMMM", culture)} a ${localizer?.format(end, "dd 'de' MMMM", culture)}`;
          }
          return `${localizer?.format(start, 'dd', culture)} a ${localizer?.format(end, "dd 'de' MMMM", culture)}`;
        },
      }}
      startAccessor={(event) => parseISO(event.localStartDate)}
      endAccessor={(event) => parseISO(event.localEndDate)}
      step={hasBigSlots ? (createButtonReservationType?.defaultDuration ?? FALLBACK_DEFAULT_DURATION) : 30}
      timeslots={1}
      messages={reservationsCalendarMessages}
      date={currentDate}
      view={currentView}
      onNavigate={handleNavigate}
      onView={handleViewChange}
      onRangeChange={handleRangeChange}
      views={calendarViews}
      {...calendarViewProps}
      max={calendarRestrictions.maxTime}
      min={calendarRestrictions.minTime}
      scrollToTime={(() => {
        const adjustedTime = new Date(new Date().setHours(new Date().getHours() - 1, 0, 0, 0));
        return adjustedTime < calendarRestrictions.minTime ? calendarRestrictions.minTime : adjustedTime;
      })()}
      eventPropGetter={eventPropGetter}
      components={{
        toolbar: CalendarToolbarComponent,
        event: CalendarEventComponent,
      }}
      onSelectEvent={(event) => onView(event)}
      slotPropGetter={slotPropGetter}
      dayPropGetter={dayPropGetter}
      // Only an employee who manages a reservation type can start one from the grid (#323).
      selectable={uniqueReservationTypes.length > 0}
      onSelectSlot={(slotInfo) => {
        if (disabledPullToRefresh && isNativePlatform) {
          setDisabledPullToRefresh(false);
        }
        if (!createButtonReservationType) {
          setCreateButtonReservationType(uniqueReservationTypes[0]);
        }

        const start = new Date(slotInfo.start);
        // Comprobar si el día está cerrado (por día de la semana o fecha específica)
        if (isClosedDay(start, calendarRestrictions)) {
          return;
        }

        // Comprobar si la hora está en periodo de descanso
        if (isInRestHour(start, calendarRestrictions.restHour)) {
          return;
        }

        // Cuando es un click, ajustar la duración a 1 hora sin exceder el límite máximo
        if (slotInfo.action === 'click') {
          const endTime = adjustEndTimeToMaxLimit(start, calendarRestrictions.maxTime);
          slotInfo.end = endTime;
        }

        start.setMilliseconds(0);
        const end = new Date(slotInfo.end);
        end.setMilliseconds(0);

        onSelectNewSlot({
          initialHours: { start: start.toISOString(), end: end.toISOString() },
          temporaryEvent: {
            id: 'temporary',
            title: 'Nueva reserva',
            start,
            end,
          },
        });
      }}
      onSelecting={(event) => {
        if (hasBigSlots) {
          const duration = differenceInMinutes(event.end, event.start);
          if (duration > (createButtonReservationType?.defaultDuration ?? FALLBACK_DEFAULT_DURATION)) {
            return false;
          }
        }

        if (!disabledPullToRefresh && isNativePlatform) {
          setDisabledPullToRefresh(true);
        }
        return true;
      }}
    />
  );
};
